import { ICellRendererParams, ValueGetterParams } from 'ag-grid-community'

import { DeployUiState, StoreWithDeployStatus } from '@/models/operations/types'
import { Badge } from '@/components'
import { getStoreReadyIso } from '@/services/operations/storeUtils'
import { formatOpsDateTime, formatRelativeSince } from '@/services/operations'
import { classNames } from '@/utils'

import { OpsStoreActionButtons } from '../components/OpsStoreActionButtons'

const DEPLOY_STATUS_LABELS: Record<DeployUiState, string> = {
  running: 'READY',
  building: 'BUILDING',
  failed: 'FAILED',
}

function getDeployBadgeVariant(state: DeployUiState) {
  switch (state) {
    case 'running':
      return { isSuccess: true as const }
    case 'building':
      return { isInfo: true as const }
    case 'failed':
      return { isDanger: true as const }
  }
}

function StoreStatusBadge({ state }: { state: DeployUiState }) {
  return (
    <Badge
      className="w-fit text-sm uppercase shrink-0"
      {...getDeployBadgeVariant(state)}
    >
      {DEPLOY_STATUS_LABELS[state]}
    </Badge>
  )
}

export function buildOpsStoresColumnDefs(
  onRedeploy: (store: StoreWithDeployStatus) => void,
  onClearCache: (store: StoreWithDeployStatus) => void,
  actionLoadingId?: string | null,
  actionsDisabled = false,
) {
  return [
    {
      field: 'name',
      headerName: 'Store name',
      minWidth: 180,
      cellRenderer: (props: ICellRendererParams<StoreWithDeployStatus>) => (
        <span className="font-semibold text-gray-800">{props.data?.name}</span>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Active',
      minWidth: 100,
      filter: false,
      cellRenderer: (props: ICellRendererParams<StoreWithDeployStatus>) => {
        const isActive = props.data?.isActive
        return (
          <span className="flex items-center gap-1.5 text-gray-600 h-full">
            <span
              className={classNames('w-2 h-2 rounded-full', {
                'bg-green-500': isActive,
                'bg-gray-300': !isActive,
              })}
            />
            {isActive ? 'Yes' : 'No'}
          </span>
        )
      },
    },
    {
      field: 'ready',
      headerName: 'Last updated',
      minWidth: 220,
      filter: false,
      valueGetter: (params: ValueGetterParams<StoreWithDeployStatus>) =>
        getStoreReadyIso(params.data),
      cellRenderer: (props: ICellRendererParams<StoreWithDeployStatus>) => {
        const readyIso = getStoreReadyIso(props.data)
        if (!readyIso) return <span className="text-gray-500">—</span>
        return (
          <div
            className="flex flex-col gap-0.5 whitespace-nowrap justify-center h-full"
            title={formatOpsDateTime(readyIso)}
          >
            <span className="font-medium text-gray-700 text-sm">
              {formatRelativeSince(readyIso)}
            </span>
            <span className="text-xs text-gray-400 tabular-nums">
              {formatOpsDateTime(readyIso)}
            </span>
          </div>
        )
      },
    },
    {
      field: 'deployUiState',
      headerName: 'Status',
      width: 150,
      flex: false,
      pinned: 'right' as const,
      filter: false,
      headerClass: 'ag-header-cell-center',
      cellRenderer: (props: ICellRendererParams<StoreWithDeployStatus>) => (
        <div className="flex items-center justify-center h-full w-full">
          <StoreStatusBadge state={props.data?.deployUiState ?? 'failed'} />
        </div>
      ),
    },
    {
      headerName: 'Actions',
      width: 150,
      flex: false,
      filter: false,
      sortable: false,
      pinned: 'right' as const,
      headerClass: 'ag-header-cell-center',
      cellClass: 'ops-actions-cell',
      cellRenderer: (props: ICellRendererParams<StoreWithDeployStatus>) => {
        const store = props.data
        if (!store) return null
        return (
          <div className="flex items-center justify-center h-full w-full">
            <OpsStoreActionButtons
              store={store}
              onRedeploy={onRedeploy}
              onClearCache={onClearCache}
              isLoading={actionLoadingId === store.id}
              disabled={actionsDisabled || !!actionLoadingId}
            />
          </div>
        )
      },
    },
  ]
}
